import { Box } from '@rocket/ui';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useState } from 'react';
import { useColorMode } from '../../styles/colorMode';
import { AG_GRID_STYLE_OVERRIDES, agGridTheme, COLUMN_TYPES } from './AGTable.utils';
import { ensureAgGridSetup } from './ag-grid-setup';

// biome-ignore lint/suspicious/noExplicitAny: ag-grid params, row data and colDefs are untyped legacy shapes
type AnyData = any;

interface AGTableProps {
  sx?: Record<string, AnyData>;
  rowData?: AnyData;
  colDefs?: AnyData;
  rowHeight?: number;
  rowBuffer?: number;
  isMoveable?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  isFilterFloating?: boolean;
  isSidebarVisible?: boolean;
  isGroupingVisible?: boolean;
  isAutoResize?: boolean;
  pagination?: AnyData;
  defaultPagination?: number;
  contextMenuItems?: AnyData;
  sideBarItems?: AnyData;
  onReady?: (api: AnyData) => void;
  onRowClick?: (rowIndex: number | null) => void;
  onUpdate?: (api: AnyData) => void;
  onSelect?: (rows: AnyData) => void;
  onPaginationChanged?: AnyData;
  onFilterChanged?: AnyData;
  [key: string]: AnyData;
}

// Lazily register AG Grid enterprise modules on first grid render
ensureAgGridSetup();

const AGTable = ({
  sx,
  rowData,
  colDefs,
  rowHeight = 42,
  rowBuffer = 60,
  isSelectable = false,
  isMoveable = true,
  isEditable = false,
  isFilterFloating = false,
  isSidebarVisible = false,
  isGroupingVisible = false,
  isAutoResize = false,
  pagination = false,
  defaultPagination = 500,
  sideBarItems = null,
  contextMenuItems = null,
  onReady,
  onRowClick,
  onUpdate,
  onSelect,
  onPaginationChanged,
  onFilterChanged,
  ...rest
}: AGTableProps) => {
  const { colorMode } = useColorMode();
  // AG Grid needs the columns to be stored in state
  const [AGcolDefs, setAGcolDefs] = useState(colDefs);
  useEffect(() => {
    // Update stored column definitions
    if (colDefs) {
      setAGcolDefs(colDefs);
    }
  }, [colDefs]);

  // Default column settings
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy dep array preserved verbatim
  const defaultColDef = useMemo(() => {
    const contextItems = contextMenuItems || [];
    const colDef: AnyData = {
      type: 'string',
      minWidth: 72,
      suppressMovable: !isMoveable,
      floatingFilter: isFilterFloating,
      filterParams: {
        maxNumConditions: 10,
        buttons: ['clear'],
      },
      contextMenuItems: [...contextItems, 'copy', 'export'],
    };

    // Bypass the config's context menu when the function getter is provided
    if (rest.getContextMenuItems) {
      delete colDef.contextMenuItems;
    }

    return colDef;
  }, [isMoveable, isFilterFloating, contextMenuItems]);

  // Side bar settings
  const sideBarDef = useMemo(() => {
    const sideItems = sideBarItems || [];
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressExpandAll: false,
            suppressFilterSearch: false,
          },
        },
        ...sideItems,
      ],
      defaultToolPanel: 'columns',
    };
  }, [sideBarItems]);

  // Row selection settings
  const rowSelection = useMemo((): AnyData => {
    return isSelectable
      ? {
          mode: 'multiRow',
          selectAll: 'filtered',
          enableClickSelection: false,
        }
      : {
          checkboxes: false,
          headerCheckbox: false,
          enableClickSelection: false,
          mode: 'singleRow',
        };
  }, [isSelectable]);

  return (
    <Box
      css={{
        ...AG_GRID_STYLE_OVERRIDES,
        ...sx,
      }}
    >
      <AgGridReact
        theme={agGridTheme(colorMode)}
        editType="fullRow"
        columnTypes={COLUMN_TYPES}
        columnDefs={AGcolDefs}
        suppressClickEdit={!isEditable}
        pagination={Boolean(pagination?.length)}
        paginationPageSize={defaultPagination}
        paginationPageSizeSelector={pagination}
        sideBar={isSidebarVisible ? sideBarDef : {}}
        rowGroupPanelShow={(isGroupingVisible ? 'always' : '') as AnyData}
        autoSizeStrategy={isAutoResize ? { type: 'fitCellContents' } : undefined}
        onGridReady={onReady}
        onRowClicked={(e: AnyData) => onRowClick?.(e.rowIndex)}
        onModelUpdated={(e: AnyData) => onUpdate?.(e.api)}
        onSelectionChanged={(e: AnyData) => onSelect?.(e.api.getSelectedRows())}
        onPaginationChanged={onPaginationChanged}
        onFilterChanged={onFilterChanged}
        {...{ defaultColDef, rowHeight, rowBuffer, rowData, rowSelection }}
        {...rest}
      />
    </Box>
  );
};

export default AGTable;
